import { Checkbox } from 'antd';
import { type CheckboxGroupProps } from 'antd/es/checkbox';
import clx from 'classnames';
import { omit } from 'lodash';
import { useCallback } from 'react';
import styled from 'styled-components';
import { useInputController } from '../hooks/useInputController.ts';
import { IsRadioOrCheckboxGroupElementSymbol } from '../item/symbols/isRadioOrCheckboxGroupElement.tsx';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility.tsx';
import { useFormItemEnabled } from '../item/useFormItemEnabled.ts';

const StyledCheckboxGroup = styled(Checkbox.Group)`
  flex-flow: wrap;
  &.vertical {
    flex-direction: column;
    > label {
      width: max-content;
      max-width: 100%;
    }
  }
  &.read-only {
    pointer-events: none;
  }
`;

export interface ICheckboxGroupInputProps
  extends Omit<CheckboxGroupProps, 'name'> {
  name: string;
  /**
   * Permette lo sviluppo in verticale delle opzioni
   * -> un opzione per ogni riga
   */
  vertical?: boolean; // TODO: da implementare
  /**
   * Aggungiamo proprietà readOnly non gestita da antd
   */
  readOnly?: boolean;
  /**
   * Permette la divisione in colonne delle opzioni
   * -> Numero di opzioni per ogni riga
   */
  // maxOptionsInRow?: 2 | 3 | 4 | 5;
  /**
   * Forza la dimensione delle opzioni
   */
  // forceMaxOptions?: boolean;
}

export function CheckboxGroupInput(props: ICheckboxGroupInputProps) {
  const { name, vertical, readOnly, className, ...inputProps } = props;

  const { enabled, visuallyDisabled } = useFormItemEnabled();

  const {
    field: { value, onChange, onBlur, ref },
  } = useInputController(name, { enabled });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <tbd>
  const handleChange = useCallback(
    (e: any[]) => onChange(e as string[]),
    [name],
  );
  const accessibility = useFormItemAccessibility();

  return (
    <div
      role="group"
      {...omit(accessibility.attributes, 'id')}
      aria-labelledby={accessibility.labelId}
    >
      <StyledCheckboxGroup
        {...inputProps}
        value={value}
        disabled={visuallyDisabled}
        ref={ref}
        onChange={readOnly ? undefined : handleChange}
        className={clx(className, { vertical, 'read-only': readOnly })}
      />
    </div>
  );
}

CheckboxGroupInput[IsRadioOrCheckboxGroupElementSymbol] = true;
